import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const API_URL = config.backendUrl

export const fetchHardware = createAsyncThunk(
  "hardware/fetchHardware",
  async () => {
    const response = await axiosInstance.get(`${API_URL}/hardwaredatastore/all`);
    return response.data;
  }
);

export const addHardware = createAsyncThunk(
  "hardware/addHardware",
  async (newData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${API_URL}/hardwaredatastore/save`,
        newData
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data); // contains { message: "Part description already exists." }
      }
      return rejectWithValue({ message: err.message || "Something went wrong" });
    }
  }
);

export const updateHardware = createAsyncThunk(
  "hardware/updateHardware",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_URL}/hardwaredatastore/update/${id}`,
        updatedData
      );
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data); // { message: "Part description already exists." }
      }
      return rejectWithValue({ message: err.message || "Something went wrong" });
    }
  }
);

export const deleteHardware = createAsyncThunk(
  "hardware/deleteHardware",
  async (id) => {
    await axiosInstance.delete(`${API_URL}/hardwaredatastore/delete/${id}`);
    return id;
  }
);

export const importHardwareFromExcel = createAsyncThunk(
  "hardware/importHardwareFromExcel",
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(`${API_URL}/hardwaredatastore/import`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "File import failed");
    }
  }
);

const hardwareSlice = createSlice({
  name: "hardware",
  initialState: {
    data: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetHardwareState: (state) => {
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchHardware.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHardware.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchHardware.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(addHardware.fulfilled, (state, action) => {
        if (Array.isArray(state.data)) {
          state.data.push(action.payload);
        } else {
          state.data = [action.payload]; // fallback
        }
      })

      .addCase(updateHardware.fulfilled, (state, action) => {
        if (Array.isArray(state.data)) {
          const index = state.data.findIndex(
            (item) => item._id === action.payload._id
          );
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        } else {
          state.data = [action.payload];
        }
      })

      .addCase(deleteHardware.fulfilled, (state, action) => {
        if (Array.isArray(state.data)) {
          state.data = state.data.filter((item) => item._id !== action.payload);
        } else {
          state.data = []; // Or handle appropriately
        }
      });
  },
});

export const { resetHardwareState } = hardwareSlice.actions;
export default hardwareSlice.reducer;
